import { BotPersonalDataForm } from "@closerplatform/spinner-openapi";
import { CheckboxInput } from "@fidget/common/components/form/checkboxInput";
import { EditInput } from "@fidget/common/components/form/editInput";
import { EditTextarea } from "@fidget/common/components/form/editTextarea";
import {
  LanguageOption,
  LanguagePicker
} from "@fidget/common/components/form/languagePicker/languagePicker";
import { CloserLogo } from "@fidget/common/components/image/closerLogo/closerLogo";
import { AgentButton } from "@fidget/common/components/inputs/buttons/agentButton/agentButton";
import { staticConfig } from "@fidget/common/config/static-config";
import { L10n } from "@fidget/common/i18n";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { unwrapFormState } from "@fidget/common/utils/unwrapFormState";
import { FieldState, FormState } from "formstate";
import { isEmpty } from "lodash";
import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import LektaLogo from "../../../assets/lekta_logo.svg";
import commonStyles from "../../../containers/settings/settingsEditCommon.css";
import { useStore } from "../../../models";
import { useAgentServices } from "../../../models/agentServices";
import { CloserBotOrgConfigFields } from "../../../models/currentUser/OrgConfig";
import { EventActionsBotSettings } from "../eventActionsBot/eventActionsBotSettings";
import { SettingsForm } from "../settingsForm";
import { SubtitleDescription } from "../subtitleDescription";
import { BotCard } from "./botCard";
import styles from "./botsSettings.css";
import { useEventActionsSettings } from "./useEventActionsSettings";

export const BotsSettings = observer(() => {
  const {
    currentBot,
    setCurrentBot,
    currentLanguage,
    setCurrentLanguage,
    botSettingsForm,
    submitAll,
    discardAll,
    isBeingEdited,
    botPersonalDataForm
  } = botsSettingsState();

  const renderActiveBotSelection = () => (
    <div className={commonStyles.section}>
      <SubtitleDescription
        subtitle={intl.formatMessage("settings.bots.botsList.title")}
        description={intl.formatMessage("settings.bots.botsList.description")}
      />
      <div className={commonStyles.content}>
        <div className={styles["bot-list"]}>
          <BotCard
            title="Closer Bot"
            isActive={currentBot === BotType.CloserBot}
            onClick={() => setCurrentBot(BotType.CloserBot)}
            logoElement={<CloserLogo />}
          />
          <BotCard
            title="Lekta"
            isActive={currentBot === BotType.Lekta}
            onClick={() => setCurrentBot(BotType.Lekta)}
            logoElement={<img src={LektaLogo} />}
          />
        </div>
      </div>
    </div>
  );

  const renderPersonalDataSection = () => {
    const firstNameField = botPersonalDataForm.$.firstName;
    const lastNameField = botPersonalDataForm.$.lastName;
    return (
      <div className={commonStyles.section}>
        <SubtitleDescription
          subtitle={intl.formatMessage("settings.bots.personalData.title")}
          description={intl.formatMessage("settings.bots.personalData.description")}
        />
        <div className={commonStyles.content}>
          <p className={styles.label}>
            {intl.formatMessage("settings.bots.personalData.firstName.placeholder")}
          </p>
          <EditInput
            className={styles["text-input"]}
            value={firstNameField.value}
            onChange={(ev: React.FormEvent<HTMLInputElement>) =>
              botPersonalDataForm.$.firstName.onChange((ev.target as HTMLInputElement).value)
            }
            hasError={firstNameField.hasError}
            errorHint={firstNameField.error}
            placeholder={intl.formatMessage("settings.bots.personalData.firstName.placeholder")}
          />
          <p className={styles.label}>
            {intl.formatMessage("settings.bots.personalData.lastName.placeholder")}
          </p>
          <EditInput
            className={styles["text-input"]}
            value={lastNameField.value}
            onChange={(ev: React.FormEvent<HTMLInputElement>) =>
              botPersonalDataForm.$.lastName.onChange((ev.target as HTMLInputElement).value)
            }
            hasError={lastNameField.hasError}
            errorHint={lastNameField.error}
            placeholder={intl.formatMessage("settings.bots.personalData.lastName.placeholder")}
          />
        </div>
      </div>
    );
  };

  const renderConsentSection = () => {
    const onAgreementChange = (ev: React.FormEvent<HTMLTextAreaElement>) => {
      botSettingsForm.$.agreement.onChange(ev.currentTarget.value);
    };

    const renderConsentMessageInput = () => (
      <EditTextarea
        value={botSettingsForm.$.agreement.value}
        onChange={onAgreementChange}
        className={commonStyles.input}
        disabled={!botSettingsForm.$.agreementEnabled.value}
        placeholder={intl.formatMessage("settings.bots.messageInput.placeholder")}
        hasError={botSettingsForm.$.agreement.hasError}
        errorHint={botSettingsForm.$.agreement.error}
      />
    );

    return (
      <div className={commonStyles.section}>
        <SubtitleDescription
          subtitle={intl.formatMessage("settings.bots.activeBotSettings.consent.title")}
          description={intl.formatMessage("settings.bots.activeBotSettings.consent.description")}
        />
        <div className={commonStyles.content}>
          <CheckboxInput
            value={botSettingsForm.$.agreementEnabled.value}
            id={"consent_enabled"}
            label={intl.formatMessage("settings.bots.activeBotSettings.consent.enabledLabel")}
            onChange={botSettingsForm.$.agreementEnabled.onChange}
          />
          {renderConsentMessageInput()}
        </div>
      </div>
    );
  };

  const renderWelcomeMessageSection = () => {
    const renderWelcomeMessageInput = () => {
      const formField =
        currentLanguage === LanguageOption.English
          ? botSettingsForm.$.welcomeMessageEn
          : botSettingsForm.$.welcomeMessagePl;
      return (
        <EditInput
          className={styles["text-input"]}
          value={formField.value}
          onChange={(ev: React.FormEvent<HTMLInputElement>) =>
            formField.onChange((ev.target as HTMLInputElement).value)
          }
          hasError={formField.hasError}
          errorHint={formField.error}
          placeholder={intl.formatMessage("settings.bots.messageInput.placeholder")}
        />
      );
    };

    return (
      <div className={commonStyles.section}>
        <SubtitleDescription
          subtitle={intl.formatMessage("settings.bots.activeBotSettings.welcomeMessage.title")}
          description={intl.formatMessage(
            "settings.bots.activeBotSettings.welcomeMessage.description"
          )}
        />
        <div className={commonStyles.content}>
          <CheckboxInput
            value={botSettingsForm.$.welcomeMessageEnabled.value}
            id={"welcome_message_enabled"}
            label={intl.formatMessage("settings.bots.activeBotSettings.welcomeMessage.checkbox")}
            onChange={botSettingsForm.$.welcomeMessageEnabled.onChange}
          />
          {renderWelcomeMessageInput()}
        </div>
      </div>
    );
  };

  const renderCloserBotSettings = () => (
    <>
      <div className={commonStyles.section}>
        <SubtitleDescription
          subtitle={intl.formatMessage("settings.bots.activeBotSettings.language.title")}
          description={intl.formatMessage("settings.bots.activeBotSettings.language.description")}
        />
        <div className={commonStyles.content}>
          <LanguagePicker
            onChange={(val) => (val ? setCurrentLanguage(val) : undefined)}
            currentLanguage={currentLanguage}
          />
        </div>
      </div>
      {renderPersonalDataSection()}
      {renderWelcomeMessageSection()}
      {renderConsentSection()}
      <EventActionsBotSettings />
    </>
  );

  const renderContactUs = () => (
    <div className={commonStyles.section}>
      <SubtitleDescription
        subtitle={intl.formatMessage("settings.bots.activeBotSettings.configure")}
        description={intl.formatMessage("settings.bots.activeBotSettings.configureDescription", {
          name: currentBot
        })}
      />
      <div className={commonStyles.content}>
        <AgentButton
          onClick={() => (window.location.href = `mailto:${staticConfig.closerSupportEmail}`)}
        >
          {intl.formatMessage("settings.bots.activeBotSettings.contactUs")}
        </AgentButton>
      </div>
    </div>
  );

  const renderActiveBotSettingsSection = () =>
    currentBot === BotType.CloserBot ? renderCloserBotSettings() : renderContactUs();

  return (
    <>
      <SettingsForm
        onDiscard={discardAll}
        disabled={!isBeingEdited}
        onSubmit={submitAll}
        withoutSaveDiscardButtons={currentBot !== BotType.CloserBot}
      >
        <div className={commonStyles.title}>
          <L10n id={"settings.bots.activeBot.title"} />
        </div>
        {renderActiveBotSelection()}
        {renderActiveBotSettingsSection()}
      </SettingsForm>
    </>
  );
});

type BotSettingsFields = {
  welcomeMessageEnabled: FieldState<boolean>;
  welcomeMessagePl: FieldState<string>;
  welcomeMessageEn: FieldState<string>;
  agreement: FieldState<string>;
  agreementEnabled: FieldState<boolean>;
};

type BotSettingsFormState = FormState<BotSettingsFields>;

const botsSettingsState = () => {
  const getInitialForm = (): BotSettingsFormState => {
    const { orgConfig } = currentUserStore;
    const initial = {
      welcomeMessageEnabled: orgConfig?.welcomeMessageEnabled,
      welcomeMessagePl: orgConfig?.welcomeMessagePl,
      welcomeMessageEn: orgConfig?.welcomeMessageEn,
      agreement: orgConfig?.agreement,
      agreementEnabled: orgConfig?.agreementEnabled
    };

    return new FormState({
      welcomeMessageEnabled: new FieldState(initial.welcomeMessageEnabled || false),
      welcomeMessagePl: new FieldState(initial.welcomeMessagePl || ""),
      welcomeMessageEn: new FieldState(initial.welcomeMessageEn || ""),
      agreement: new FieldState(initial.agreement || ""),
      agreementEnabled: new FieldState(initial.agreementEnabled || false)
    });
  };

  const { currentUserStore, uiStore } = useStore();
  const { spinnerService } = useAgentServices();

  const [isBeingEdited, setIsBeingEdited] = useState<boolean>();
  const [currentBot, setCurrentBot] = useState<BotType>(BotType.CloserBot);
  const [botSettingsForm, setBotSettingsForm] = useState<BotSettingsFormState>(getInitialForm);
  const {
    onSubmitAll: eventActionsSubmit,
    onDiscardAll: eventActionsDiscard,
    items,
    editedItems,
    setItemEdited,
    currentLanguage,
    setCurrentLanguage
  } = useEventActionsSettings();
  const { form: botPersonalDataForm, onReset: botPersonalDataFormReset } = useBotPersonalDataForm();

  const submitAll = async () => {
    const validated = await botSettingsForm.validate();
    const botPersonalDataValidated = await botPersonalDataForm.validate();
    if (validated.hasError || botPersonalDataValidated.hasError) {
      uiStore.addErrorToast(intl.formatMessage("settings.errorInForm"));
      return;
    }

    const data = unwrapFormState<CloserBotOrgConfigFields>(validated.value, true);
    const botPersonalData = unwrapFormState<BotPersonalDataForm>(
      botPersonalDataValidated.value,
      true
    );

    if (currentUserStore.orgConfig) {
      try {
        eventActionsSubmit();
        await currentUserStore.orgConfig.update(data);
        await spinnerService.updateBotPersonalData(botPersonalData);
        setIsBeingEdited(false);
      } catch (e) {
        uiStore.addErrorToast(intl.formatMessage("error.something"));
      }
    }
  };

  const makeSubmittable = () => setIsBeingEdited(true);

  useEffect(() => {
    items.forEach((item) => {
      const callback = () => {
        setItemEdited(item);
        makeSubmittable();
      };

      item.$.enabled.onUpdate(callback);
      item.$.event.onUpdate(callback);
      item.$.response.onUpdate(callback);
      item.$.tagGroupId.onUpdate(callback);
    });
  }, [items]);

  useEffect(() => {
    botPersonalDataForm.$.firstName.onUpdate(makeSubmittable);
    botPersonalDataForm.$.lastName.onUpdate(makeSubmittable);
  }, [botPersonalDataForm]);

  useEffect(() => {
    botSettingsForm.$.welcomeMessageEnabled.onUpdate(makeSubmittable);
    botSettingsForm.$.welcomeMessagePl.onUpdate(makeSubmittable);
    botSettingsForm.$.welcomeMessageEn.onUpdate(makeSubmittable);
    botSettingsForm.$.agreement.onUpdate(makeSubmittable);
    botSettingsForm.$.agreementEnabled.onUpdate(makeSubmittable);
  }, [botSettingsForm]);

  useEffect(() => {
    if (!isEmpty(editedItems)) {
      setIsBeingEdited(true);
    }
  }, [editedItems]);

  const discardAll = () => {
    eventActionsDiscard();
    setBotSettingsForm(getInitialForm);
    botPersonalDataFormReset();
    setIsBeingEdited(false);
  };

  return {
    currentBot,
    setCurrentBot,
    currentLanguage,
    setCurrentLanguage,
    botSettingsForm,
    submitAll,
    discardAll,
    isBeingEdited,
    botPersonalDataForm
  };
};

const useBotPersonalDataForm = () => {
  const { agentStore } = useStore();
  const initialFormData = () => {
    const profile = agentStore.orgBot;
    return new FormState({
      firstName: new FieldState(profile?.firstName ?? ""),
      lastName: new FieldState(profile?.lastName ?? "")
    });
  };

  const [form, setForm] = useState(initialFormData);

  const onReset = () => setForm(initialFormData);

  return {
    form,
    onReset
  };
};

enum BotType {
  CloserBot = "Closer Bot",
  Lekta = "Lekta"
}
